import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontalIcon, PlusIcon, TicketPercentIcon, XIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@ems/ui/components/tooltip'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { FilteredEmpty } from '#admin/components/list-filter.tsx'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { OptionSelect } from '@ems/ui/patterns/option-select'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import {
  discountLabel,
  usesLabel,
  validityLabel,
  type Voucher,
} from '#admin/features/vouchers/api.ts'
import {
  matchesVoucher,
  parseVoucherSearch,
  VOUCHER_STATES,
  voucherState,
} from '#admin/features/vouchers/filter.ts'
import { useDeleteVoucher, useSetVoucherActive } from '#admin/features/vouchers/mutations.ts'
import { vouchersQuery } from '#admin/features/vouchers/queries.ts'
import { VoucherDialog } from '#admin/features/vouchers/voucher-dialog.tsx'
import { VouchersSkeleton } from '#admin/features/vouchers/vouchers-skeleton.tsx'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'

/**
 * `/ticketing/vouchers` — the edition's discount codes.
 *
 * Beside the ticket types, whose list is the voucher's scope picker. Guarded on `ticket.view`;
 * without `ticket.manage` the write controls are removed, as on the ticket types. The search and
 * the status filter live in the URL.
 */
export const Route = createFileRoute('/_authenticated/ticketing/vouchers')({
  pendingComponent: VouchersSkeleton,
  validateSearch: parseVoucherSearch,
  staticData: { crumb: { parents: [{ label: 'Ticketing' }], label: 'Vouchers' } },
  loader: async ({ context }) => {
    const [session, manifest] = await Promise.all([
      context.queryClient.ensureQueryData(sessionQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ])
    if (manifest === null || !can(session, 'ticket.view')) throw notFound()
    await Promise.all([
      context.queryClient.ensureQueryData(vouchersQuery()),
      context.queryClient.ensureQueryData(ticketTypesQuery()),
    ])
  },
  notFoundComponent: NotFoundView,
  component: VouchersPage,
})

type Editing = { voucher: Voucher | null } | null

/** `all` rather than `''`: Radix reserves the empty string for "nothing chosen". */
const ANY = 'all'

const STATE_LABEL = { active: 'Active', inactive: 'Inactive' } as const

function VouchersPage() {
  const { data } = useSuspenseQuery(vouchersQuery())
  const { data: ticketTypes } = useSuspenseQuery(ticketTypesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  // The loader refused a null manifest; the fallback only satisfies the type.
  const zone = manifest?.tenant.timezone ?? 'UTC'
  const canManage = can(session, 'ticket.manage')

  const [editing, setEditing] = useState<Editing>(null)
  const [deleting, setDeleting] = useState<Voucher | null>(null)
  const { mutate: toggleActive, isPending: toggling } = useSetVoucherActive()
  const remove = useDeleteVoucher(() => setDeleting(null))

  const active = data.items.filter((voucher) => voucher.isActive).length

  // Filtered in the browser: the list is already here in full.
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useLiveSearch(
    search.q ?? '',
    (term) =>
      void navigate({ search: (prev) => parseVoucherSearch({ ...prev, q: term }), replace: true }),
  )
  const shown = data.items.filter((voucher) => matchesVoucher(voucher, search))
  const filtered = search.q !== undefined || search.status !== undefined
  // One page at a time. A new search or status starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${search.status ?? ''}|${search.q ?? ''}`,
  )
  const paged = pageSlice(shown, page, pageSize)
  const clear = () => {
    setDraft('')
    void navigate({ search: {} })
  }

  const columns = useMemo<ColumnDef<Voucher, never>[]>(() => {
    // One clock per render, so every row's validity is judged at the same instant.
    const now = new Date()
    const typeName = new Map(ticketTypes.items.map((type) => [type.id, type.name]))
    return [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      { accessorKey: 'name', header: 'Name', meta: { primary: true } },
      {
        id: 'discount',
        header: 'Discount',
        meta: { align: 'end' },
        cell: ({ row }) => discountLabel(row.original),
      },
      {
        id: 'uses',
        header: 'Uses',
        cell: ({ row }) => <span className="text-xs tabular-nums">{usesLabel(row.original)}</span>,
      },
      {
        id: 'validity',
        header: 'Validity',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {validityLabel(row.original, now, zone)}
          </span>
        ),
      },
      {
        id: 'scope',
        header: 'Applies to',
        cell: ({ row }) => {
          const ids = row.original.ticketTypeIds
          if (ids.length === 0) {
            return <span className="text-muted-foreground text-xs">All ticket types</span>
          }
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-xs underline decoration-dotted">
                  {ids.length} ticket {ids.length === 1 ? 'type' : 'types'}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {ids.map((id) => typeName.get(id) ?? 'A deleted type').join(', ')}
              </TooltipContent>
            </Tooltip>
          )
        },
      },
      {
        id: 'active',
        header: 'State',
        cell: ({ row }) =>
          row.original.isActive ? (
            <StatusBadge tone="success">active</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">inactive</StatusBadge>
          ),
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const },
              cell: ({ row }: { row: { original: Voucher } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${row.original.code}`}
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing({ voucher: row.original })}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={toggling}
                      onSelect={() =>
                        toggleActive({ voucher: row.original, isActive: !row.original.isActive })
                      }
                    >
                      {row.original.isActive ? 'Deactivate' : 'Activate'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(row.original)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies ColumnDef<Voucher, never>,
          ]
        : []),
    ]
  }, [canManage, ticketTypes.items, toggleActive, toggling, zone])

  // The loader refused a null manifest; this narrows it for the dialog.
  if (manifest === null) return null

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Vouchers"
        description={`Discount codes · ${active} of ${data.items.length} active`}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ voucher: null })}>
              <PlusIcon />
              Add voucher
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the vouchers" />}

      {data.items.length === 0 ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
            <SearchInput
              aria-label="Search by code or name"
              placeholder="Search code or name"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <OptionSelect
            aria-label="Status"
            className="w-full sm:w-44"
            value={search.status ?? ANY}
            onValueChange={(value) =>
              void navigate({ search: (prev) => parseVoucherSearch({ ...prev, status: value }) })
            }
            options={[
              { value: ANY, label: 'All' },
              ...VOUCHER_STATES.map((value) => ({
                value,
                label: `${STATE_LABEL[value]} · ${data.items.filter((voucher) => voucherState(voucher) === value).length}`,
              })),
            ]}
          />
          {/* Beside the filters it clears, and only while one is on. */}
          {filtered ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
            >
              <XIcon className="size-4" />
              Clear filters
            </Button>
          ) : null}
        </div>
      )}

      <DataTable
        caption="Vouchers"
        title={`${filtered ? 'Matching vouchers' : 'All vouchers'} (${shown.length})`}
        // Only with rows: an empty table shows its empty state, and no line under it.
        footer={
          shown.length === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={shown.length}
              onPageChange={setPage}
              ariaLabel="Pages of the vouchers"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(voucher) => voucher.id}
        empty={
          data.items.length > 0 ? (
            <FilteredEmpty
              message={
                search.q === undefined
                  ? 'No vouchers in this status'
                  : `No vouchers matching “${search.q}”`
              }
              onClear={clear}
            />
          ) : (
            <EmptyState
              icon={<TicketPercentIcon />}
              title="No vouchers yet"
              description="Add a code for an early-bird price, a partner discount or a complimentary group."
            />
          )
        }
      />

      {editing === null ? null : (
        <VoucherDialog
          // Keyed, so opening another voucher starts from its own values rather than the last edit.
          key={editing.voucher?.id ?? 'new'}
          voucher={editing.voucher}
          ticketTypes={ticketTypes.items}
          currency={manifest.tenant.currencyCode}
          zone={manifest.tenant.timezone}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={`Delete ${deleting?.code ?? ''}?`}
        description="Nobody can apply it any more, and the code is free to reuse. Registrations it already discounted keep their discount."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => (deleting === null ? undefined : remove.mutate(deleting))}
      />
    </div>
  )
}
